import {
  Breadcrumbs,
  DetailPageHeader,
  DetailPageHero,
  PageContainer,
  StatusPill,
  useApiClient,
  useRequireOrganization,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { Activity, Mail, Shield, User } from 'lucide-react';
import { useEffect } from 'react';
import {
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

import type { UserScanHistory } from './useHotelUserContext';

function getUserDisplayName(user: UserScanHistory['user']): string {
  if (user.firstName || user.lastName) {
    return `${user.firstName || ''} ${user.lastName || ''}`.trim();
  }
  return user.email;
}

function getAccessStatus(scans: UserScanHistory['scans']): boolean {
  if (!scans.length) return false;
  const mostRecentScan = scans[0];
  return mostRecentScan.expiresAt ? new Date(mostRecentScan.expiresAt) > new Date() : true;
}

const tabs = [
  { label: 'Overview', path: 'overview', icon: User },
  { label: 'Scans', path: 'scans', icon: Shield },
  { label: 'Promotional Offers', path: 'offers', icon: Mail },
];

const internalTabs = [
  { label: 'Activity', path: 'activity', icon: Activity },
];

export function HotelUserDetailPage() {
  const { hotelId, userId } = useParams<{ hotelId: string; userId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClient = useApiClient();
  const { isInternal } = useRequireOrganization();

  const { data: hotelData } = useQuery<{ success: boolean; data: { id: string; name: string } }>({
    queryKey: ['Hotel', hotelId],
    queryFn: () => apiClient.get(`/hotels/${hotelId}`),
    enabled: !!hotelId,
  });

  const { data, isLoading, error } = useQuery<{ success: boolean; data: UserScanHistory }>({
    queryKey: ['scanner-history', hotelId, userId],
    queryFn: () => apiClient.get(`/hotels/${hotelId}/scanners/${userId}`),
    enabled: !!hotelId && !!userId,
  });

  const hotelName = hotelData?.data?.name || 'Hotel';
  const userData = data?.data;
  const isActive = userData ? getAccessStatus(userData.scans) : false;

  const allTabs = isInternal ? [...tabs, ...internalTabs] : tabs;

  // Redirect base path to /overview
  useEffect(() => {
    if (hotelId && userId) {
      const basePath = `/hotels/${hotelId}/users/${userId}`;
      if (location.pathname === basePath || location.pathname === `${basePath}/`) {
        navigate(`${basePath}/overview`, { replace: true });
      }
    }
  }, [hotelId, userId, location.pathname, navigate]);

  const breadcrumbs = [
    { label: 'Home', href: '/' },
    { label: 'Hotels', href: '/hotels' },
    { label: hotelName, href: `/hotels/${hotelId}` },
    { label: 'Guests', href: `/hotels/${hotelId}` },
    { label: userData ? getUserDisplayName(userData.user) : 'Guest', current: true },
  ];

  const basePath = `/hotels/${hotelId}/users/${userId}`;

  if (!hotelId || !userId) {
    return (
      <PageContainer>
        <div className="text-center py-8">
          <h2 className="text-lg font-medium mb-2">Guest Not Found</h2>
          <p className="text-muted-foreground">The requested guest could not be found.</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer padding="none" className="flex flex-col overflow-hidden">
      <DetailPageHeader>
        <Breadcrumbs items={breadcrumbs} />
      </DetailPageHeader>

      {!isLoading && error ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center py-12 text-destructive">
            Failed to load guest information
          </div>
        </div>
      ) : (
        <DetailPageHero
          isLoading={isLoading}
          category={{ label: 'Guest', icon: User }}
          title={userData ? getUserDisplayName(userData.user) : ''}
          image={{
            url: userData?.user.imageUrl ?? null,
            alt: userData ? getUserDisplayName(userData.user) : 'Guest',
            fallbackIcon: User,
            shape: 'avatar',
          }}
          meta={
            userData ? (
              <>
                <span className="text-white/80">{userData.user.email}</span>
                {hotelName && <span>{hotelName}</span>}
              </>
            ) : undefined
          }
          badges={
            userData ? (
              <StatusPill
                status={isActive ? 'published' : 'needs_review'}
                label={isActive ? 'Active' : 'Expired'}
              />
            ) : undefined
          }
          tabs={allTabs}
          basePath={basePath}
        >
          {userData ? <Outlet context={{ userData, isLoading, hotelId }} /> : null}
        </DetailPageHero>
      )}
    </PageContainer>
  );
}
